import type { TeamsSendRequest } from "../api";
import {
	Button,
	Menu,
	MultiSelect,
	ScrollArea,
	Stack,
	Text,
	Group,
	Radio,
	TextInput,
	MultiSelectProps,
	type ScrollAreaAutosizeProps,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconChevronDown, IconSend } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { modals } from "@mantine/modals";
import { TeamsMessageEditor } from "./TeamsMessageEditor";
import { useTeamsMessageEditor } from "./useTeamsMessageEditor";
import { useSendTeamsMessageProxy, useStaffOidsSuspense } from "../query";
import { useResolveStudents } from "@features/communications/queries";
import {
	RecipientResolutionModal,
	type RecipientResolutionModalView,
} from "@features/communications/components/RecipientResolutionModal";
import classes from "./SendTeamsMessageForm.module.css";
import { TeamsMessage } from "@godalming/api-types";
import { StudentRecipientSelect } from "@features/student-lists/components/StudentRecipientSelect";

export type TeamsStudent = {
	studentNumber: string;
	firstName: string;
	surname: string;
	uuid: string | null;
};

interface SendTeamsMessageFormProps {
	defaultSelectedStudents?: string[];
	defaultSelectedStaff?: string[];
	suggestedStudents?: string[];
	defaultType?: "oneOnOne" | "group";
	allowSelectionChange?: boolean;
	suggestedRecipients?: string;
	onSuccess?: () => void;
}

const toStudentSelector = (value: string) =>
	value.toLowerCase().startsWith("student:") ? value : `student:${value}`;

export function TeamsMessageModalScrollArea(props: ScrollAreaAutosizeProps) {
	return <ScrollArea.Autosize {...props} type="always" scrollbars="y" />;
}

export const SendTeamsMessageForm: React.FC<SendTeamsMessageFormProps> = ({
	defaultSelectedStudents = [],
	defaultSelectedStaff = [],
	suggestedStudents,
	defaultType = "oneOnOne",
	allowSelectionChange = true,
	onSuccess,
}) => {
	const form = useForm<TeamsMessage>({
		initialValues: {
			type: defaultType,
			studentRecipients: defaultSelectedStudents.map(toStudentSelector),
			staffRecipients: [...defaultSelectedStaff],
			message: "",
			sendAs: "self",
		},
	});

	const renderTagsInputOption: MultiSelectProps["renderOption"] = ({
		option,
	}) => <Text size="sm">{option.label}</Text>;

	const sendMessageMutation = useSendTeamsMessageProxy();
	const editor = useTeamsMessageEditor(sendMessageMutation.isPending);

	const [recipientError, setRecipientError] = useState<string | null>(null);
	const [messageError, setMessageError] = useState<string | null>(null);
	const [resolutionModalView, setResolutionModalView] =
		useState<RecipientResolutionModalView | null>(null);
	const studentResolutionQuery = useResolveStudents({
		selectors: form.values.studentRecipients,
	});

	const resolvedStudentRecipients = (
		studentResolutionQuery.data?.students ?? []
	)
		.filter((student) => student.entraObjectId)
		.map((student) => String(student.studentNumber));

	useEffect(() => {
		if (
			form.values.studentRecipients.length +
				form.values.staffRecipients.length >
			0
		) {
			setRecipientError(null);
		}
	}, [
		form.values.staffRecipients.length,
		form.values.studentRecipients.length,
	]);

	function hasMessageContent(message: string) {
		return (
			message
				.replace(/<[^>]*>/g, "")
				.replace(/&nbsp;/g, " ")
				.trim().length > 0
		);
	}

	function sendMessage(params: TeamsSendRequest) {
		sendMessageMutation.mutate(params, {
			onSuccess: (result) => {
				if (result.sentCount === 0) return;
				form.reset();
				editor?.commands.setContent("");
				onSuccess?.();
			},
		});
	}

	async function handleSubmit(values: typeof form.values) {
		const message = editor?.getHTML() ?? "";
		const recipientCount =
			resolvedStudentRecipients.length + values.staffRecipients.length;
		const nextRecipientError =
			recipientCount === 0 ? "Select at least one recipient." : null;
		const nextMessageError = hasMessageContent(message)
			? null
			: "Enter a message.";

		setRecipientError(nextRecipientError);
		setMessageError(nextMessageError);

		if (nextRecipientError || nextMessageError) {
			return;
		}

		const params: TeamsSendRequest = {
			requestId: crypto.randomUUID(),
			contentType: "html",
			...values,
			studentRecipients: [...resolvedStudentRecipients],
			staffRecipients: [...values.staffRecipients],
			message,
		};

		const isGroupChat = params.type === "group";

		const confirmationMessage = isGroupChat
			? `Are you sure you want to create a group chat with ${recipientCount} people?`
			: `Are you sure you want to send one-on-one chats to ${recipientCount} people?`;
		const send = () => sendMessage(params);

		const openLargeSendConfirmation = () => {
			modals.openConfirmModal({
				title: "Confirm large Teams send",
				children: (
					<Text size="sm">
						{isGroupChat
							? `This will create a group chat with ${recipientCount} people.`
							: `This will send one-on-one chats to ${recipientCount} people.`}{" "}
						Are you absolutely sure?
					</Text>
				),
				labels: {
					confirm: isGroupChat ? "Create group chat" : "Send messages",
					cancel: "Cancel",
				},
				confirmProps: { leftSection: <IconSend size={16} /> },
				onConfirm: send,
			});
		};

		modals.openConfirmModal({
			title: isGroupChat ? "Create Teams group chat" : "Send Teams messages",
			children: <Text size="sm">{confirmationMessage}</Text>,
			labels: {
				confirm: isGroupChat ? "Create group chat" : "Send messages",
				cancel: "Cancel",
			},
			confirmProps: { leftSection: <IconSend size={16} /> },
			onConfirm: recipientCount > 30 ? openLargeSendConfirmation : send,
		});
	}

	const data = [
		{
			name: "One on one chats",
			description: "Send a one on one chat to each recipient.",
			value: "oneOnOne",
		},
		{
			name: "Group chat",
			description: "Create a group chat with the selected recipients.",
			value: "group",
		},
	];

	const { data: allStaffOids } = useStaffOidsSuspense();

	const staffOptions = Array.from(
		new Map(
			allStaffOids.map((oid) => [
				oid.oid,
				{
					value: oid.oid,
					label: `${oid.preferredName || oid.firstName} ${oid.surname} (${oid.initials ? oid.initials.toLowerCase() : "No initials"})`,
				},
			]),
		).values(),
	);

	const visibleSuggestedStudents = (suggestedStudents ?? [])
		.filter((studentNo) => !form.values.studentRecipients.includes(studentNo))
		.map((studentNo) => ({ value: studentNo, label: studentNo }));

	const addSuggestedStudent = (studentNo: string) => {
		form.setFieldValue("studentRecipients", [
			...form.values.studentRecipients,
			toStudentSelector(studentNo),
		]);
		setRecipientError(null);
	};

	return (
		<form id="send-teams-message-form" onSubmit={form.onSubmit(handleSubmit)}>
			<Stack>
				<Radio.Group {...form.getInputProps("type")}>
					<Group gap="xs" wrap="nowrap" align="stretch" justify="stretch">
						{data.map((item) => (
							<Radio.Card
								className={classes.root}
								value={item.value}
								key={item.value}
								p={"md"}
								py={"xs"}
							>
								<Group wrap="nowrap" align="flex-start">
									<Radio.Indicator size="sm" />
									<div>
										<Text size="sm" fw={500}>
											{item.name}
										</Text>
										<Text size="xs" c={"dimmed"}>
											{item.description}
										</Text>
									</div>
								</Group>
							</Radio.Card>
						))}
					</Group>
				</Radio.Group>
				{/* <Select 
                    label="Send as"
                    description="Choose who the message will appear from"
                    data={[
                        {
                            value: 'self',
                            label: 'Me',
                        },
                        {
                            value: 'college',
                            label: 'Godalming College'
                        }
                    ]}
                    className={'w-fit'}
                    {...form.getInputProps('sendAs')}
                /> */}
				<Stack gap={3}>
					<Stack gap={5}>
						<Group gap="xs" align="flex-end" wrap="nowrap">
							<StudentRecipientSelect
								label={`Student Recipients (${form.values.studentRecipients.length})`}
								renderOption={renderTagsInputOption}
								value={form.values.studentRecipients}
								onChange={(value) =>
									form.setFieldValue("studentRecipients", value)
								}
								disabled={
									!allowSelectionChange || sendMessageMutation.isPending
								}
								studentValuePrefix="student:"
								withCheckIcon={true}
								hidePickedOptions={true}
								styles={{
									input: {
										maxHeight: "8rem",
										overflow: "auto",
									},
								}}
								searchable
								nothingFoundMessage="No students found"
								withPillsReorder
								clearSectionMode="both"
								limit={50}
								flex={1}
							/>
							<Button
								type="button"
								variant="light"
								onClick={() => setResolutionModalView({ type: "students" })}
							>
								View students
							</Button>
						</Group>
						{visibleSuggestedStudents.length > 0 && (
							<Menu
								shadow="md"
								width={260}
								position="bottom-end"
								closeOnItemClick={false}
							>
								<Menu.Target>
									<Button
										type="button"
										size="compact-xs"
										variant="light"
										rightSection={<IconChevronDown size={14} />}
										className="w-fit! self-end"
									>
										Suggested
									</Button>
								</Menu.Target>
								<Menu.Dropdown>
									<ScrollArea.Autosize mah={260} type="auto">
										{visibleSuggestedStudents.map((student) => (
											<Menu.Item
												key={student.value}
												onClick={() => addSuggestedStudent(student.value)}
											>
												{student.label}
											</Menu.Item>
										))}
									</ScrollArea.Autosize>
								</Menu.Dropdown>
							</Menu>
						)}
					</Stack>
					<MultiSelect
						label={`Staff Recipients (${form.values.staffRecipients.length})`}
						renderOption={renderTagsInputOption}
						data={staffOptions}
						withCheckIcon={true}
						hidePickedOptions={true}
						styles={{
							input: {
								maxHeight: "8rem",
								overflow: "auto",
							},
						}}
						searchable
						nothingFoundMessage="No staff found"
						limit={50}
						withPillsReorder
						clearSectionMode="both"
						{...form.getInputProps("staffRecipients")}
					/>
					{recipientError && (
						<Text size="xs" c="red">
							{recipientError}
						</Text>
					)}
				</Stack>
				{form.values.type === "group" && (
					<TextInput
						label="Topic"
						placeholder="Enter the topic"
						{...form.getInputProps("topic")}
					/>
				)}
				<Stack gap={5}>
					<Text fw={500} size="sm">
						Message
					</Text>
					<TeamsMessageEditor editor={editor} />
					{messageError && (
						<Text size="xs" c="red">
							{messageError}
						</Text>
					)}
				</Stack>
				<Button
					type="submit"
					leftSection={<IconSend size={16} />}
					mt="md"
					loading={sendMessageMutation.isPending}
					disabled={
						studentResolutionQuery.isFetching || sendMessageMutation.isPending
					}
				>
					{form.values.type === "group" ? "Create group chat" : "Send"}
				</Button>
			</Stack>
			<RecipientResolutionModal
				view={resolutionModalView}
				onClose={() => setResolutionModalView(null)}
				students={studentResolutionQuery.data?.students ?? []}
				recipients={{ to: [], cc: [], bcc: [] }}
				loading={studentResolutionQuery.isFetching}
			/>
		</form>
	);
};
